import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiTag } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:7890/api';

const GestionCategorias = () => {
  const [activeTab, setActiveTab] = useState('posts');
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [categoriaActual, setCategoriaActual] = useState({
    id: null,
    nombre: '',
    slug: '',
    descripcion: '',
    color: '#3B82F6',
    icono: ''
  });
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasRole('administrador')) {
      navigate('/');
      return;
    }
    cargarCategorias();
  }, [activeTab]);

  const cargarCategorias = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const endpoint = activeTab === 'posts' ? '/categorias' : '/catalogos';
      
      const response = await axios.get(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCategorias(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
      alert('Error al cargar las categorías');
    } finally {
      setLoading(false);
    }
  };

  const abrirModalNuevo = () => {
    setCategoriaActual({
      id: null,
      nombre: '',
      slug: '',
      descripcion: '',
      color: '#3B82F6',
      icono: ''
    });
    setModoEdicion(false);
    setModalAbierto(true);
  };

  const abrirModalEditar = (categoria) => {
    setCategoriaActual({
      id: categoria.id,
      nombre: categoria.nombre,
      slug: categoria.slug,
      descripcion: categoria.descripcion || '',
      color: categoria.color || '#3B82F6',
      icono: categoria.icono || ''
    });
    setModoEdicion(true);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setCategoriaActual({
      id: null,
      nombre: '',
      slug: '',
      descripcion: '',
      color: '#3B82F6',
      icono: ''
    });
  };

  const generarSlug = (nombre) => {
    return nombre
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNombreChange = (e) => {
    const nombre = e.target.value;
    setCategoriaActual({
      ...categoriaActual,
      nombre,
      slug: generarSlug(nombre)
    });
  };

  const guardarCategoria = async () => {
    try {
      if (!categoriaActual.nombre.trim()) {
        alert('El nombre es requerido');
        return;
      }

      const token = localStorage.getItem('token');
      const endpoint = activeTab === 'posts' ? '/categorias' : '/catalogos';
      
      const data = {
        nombre: categoriaActual.nombre,
        slug: categoriaActual.slug,
        descripcion: categoriaActual.descripcion,
        color: categoriaActual.color,
        icono: categoriaActual.icono
      };

      if (modoEdicion && categoriaActual.id) {
        // Actualizar
        await axios.put(
          `${API_URL}${endpoint}/${categoriaActual.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Categoría actualizada exitosamente');
      } else {
        // Crear
        await axios.post(
          `${API_URL}${endpoint}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Categoría creada exitosamente');
      }

      cerrarModal();
      cargarCategorias();
    } catch (error) {
      console.error('Error al guardar categoría:', error);
      alert(error.response?.data?.message || 'Error al guardar la categoría');
    }
  };

  const eliminarCategoria = async (id) => {
    if (!confirm('¿Estás seguro de eliminar esta categoría? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const endpoint = activeTab === 'posts' ? '/categorias' : '/catalogos';
      
      await axios.delete(
        `${API_URL}${endpoint}/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Categoría eliminada exitosamente');
      cargarCategorias();
    } catch (error) {
      console.error('Error al eliminar categoría:', error);
      alert(error.response?.data?.message || 'Error al eliminar la categoría. Puede estar en uso.');
    }
  };

  const coloresPreset = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#14B8A6', '#F97316', '#6366F1'
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center">
                <FiTag className="mr-3" />
                Gestión de Categorías
              </h1>
              <p className="text-indigo-100">
                Administra las categorías de Posts (Blog) e Historicidad
              </p>
            </div>
            <button
              onClick={abrirModalNuevo}
              className="px-6 py-3 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition font-medium flex items-center"
            >
              <FiPlus className="mr-2" size={20} />
              Nueva Categoría
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('posts')}
              className={`flex-1 px-6 py-4 text-center font-medium transition ${
                activeTab === 'posts'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-indigo-600'
              }`}
            >
              📚 Categorías de Posts (Blog)
              <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                {activeTab === 'posts' ? categorias.length : ''}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('historicidad')}
              className={`flex-1 px-6 py-4 text-center font-medium transition ${
                activeTab === 'historicidad'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-purple-600'
              }`}
            >
              📖 Categorías de Historicidad
              <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                {activeTab === 'historicidad' ? categorias.length : ''}
              </span>
            </button>
          </div>
        </div>

        {/* Lista de Categorías */}
        {categorias.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FiTag className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500 text-lg mb-4">No hay categorías creadas</p>
            <button
              onClick={abrirModalNuevo}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              Crear Primera Categoría
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categorias.map((categoria) => (
              <div
                key={categoria.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: categoria.color || '#3B82F6' }}
                    >
                      {categoria.icono || categoria.nombre.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{categoria.nombre}</h3>
                      <p className="text-xs text-gray-500 font-mono">{categoria.slug}</p>
                    </div>
                  </div>
                </div>

                {categoria.descripcion && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {categoria.descripcion}
                  </p>
                )}

                {categoria.postCount !== undefined && (
                  <div className="text-sm text-gray-500 mb-4">
                    📝 {categoria.postCount || 0} posts asociados
                  </div>
                )}

                {categoria.datoCount !== undefined && (
                  <div className="text-sm text-gray-500 mb-4">
                    📖 {categoria.datoCount || 0} registros asociados
                  </div>
                )}

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => abrirModalEditar(categoria)}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium flex items-center justify-center"
                  >
                    <FiEdit2 className="mr-1" size={14} />
                    Editar
                  </button>
                  <button
                    onClick={() => eliminarCategoria(categoria.id)}
                    className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium flex items-center justify-center"
                  >
                    <FiTrash2 className="mr-1" size={14} />
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Crear/Editar */}
      {modalAbierto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">
              {modoEdicion ? 'Editar Categoría' : 'Nueva Categoría'}
            </h3>

            <div className="space-y-4">
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={categoriaActual.nombre}
                  onChange={handleNombreChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej: Atributos de Dios"
                />
              </div>

              {/* Slug (auto-generado) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slug (auto-generado)
                </label>
                <input
                  type="text"
                  value={categoriaActual.slug}
                  onChange={(e) => setCategoriaActual({ ...categoriaActual, slug: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                  placeholder="atributos-de-dios"
                />
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción (opcional)
                </label>
                <textarea
                  value={categoriaActual.descripcion}
                  onChange={(e) => setCategoriaActual({ ...categoriaActual, descripcion: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  rows="3"
                  placeholder="Breve descripción de la categoría..."
                />
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {coloresPreset.map((color) => (
                    <button
                      key={color}
                      onClick={() => setCategoriaActual({ ...categoriaActual, color })}
                      className={`w-8 h-8 rounded-full border-2 transition ${
                        categoriaActual.color === color
                          ? 'border-gray-900 scale-110'
                          : 'border-gray-300 hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <input
                  type="color"
                  value={categoriaActual.color}
                  onChange={(e) => setCategoriaActual({ ...categoriaActual, color: e.target.value })}
                  className="w-full h-10 rounded-lg border border-gray-300"
                />
              </div>

              {/* Icono/Emoji */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icono/Emoji (opcional)
                </label>
                <input
                  type="text"
                  value={categoriaActual.icono}
                  onChange={(e) => setCategoriaActual({ ...categoriaActual, icono: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-center text-2xl"
                  placeholder="📚 o texto"
                  maxLength="2"
                />
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={cerrarModal}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium flex items-center justify-center"
              >
                <FiX className="mr-2" />
                Cancelar
              </button>
              <button
                onClick={guardarCategoria}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium flex items-center justify-center"
              >
                <FiSave className="mr-2" />
                {modoEdicion ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionCategorias;







